/**
 * The status badge and its explanation, shared by every surface that lists
 * recordings — the meetings home, the media library, and the Studio picker.
 * One implementation, so "what state is this in" reads identically everywhere.
 */
import { useTranslation } from "react-i18next";
import type { RecordingSummary } from "@rec/shared";
import { Badge } from "@/components/ui";
import { i18n, serverText, useServerText } from "@/i18n";

/**
 * `processing` on an upload is ambiguous: it means both "the file is being
 * transcribed" and "the file never arrived". `startedAt` distinguishes them —
 * a genuine transcription is minutes old, anything older has stalled.
 */
const STALLED_AFTER_MS = 30 * 60 * 1000;

const BADGE_TONE: Record<string, "success" | "accent" | "warning" | "danger" | "neutral"> = {
  ready: "success",
  recording: "accent",
  processing: "warning",
  failed: "danger",
  scheduled: "neutral",
};

/** True for an upload that has been "processing" long past plausible. */
export function hasStalled(recording: RecordingSummary): boolean {
  return (
    recording.status === "processing" &&
    // Bot recordings report their own state, so a stall there is not a guess.
    !recording.statusMessage &&
    Date.now() - new Date(recording.startedAt).getTime() > STALLED_AFTER_MS
  );
}

export function StatusBadge({ recording }: { recording: RecordingSummary }) {
  const { t } = useTranslation(["recording", "common"]);
  const st = useServerText();
  if (hasStalled(recording)) return <Badge tone="danger">{t("common:status.uploadFailed")}</Badge>;

  // The provider often knows something more specific than our five states can
  // express. "Not let in" and "Password needed" are both `failed`, and a badge
  // reading "Failed" for either is true and useless.
  if (recording.statusLabel) {
    // A state that will not move without a person is amber even while it is
    // technically still "scheduled" — that is the whole signal.
    const tone =
      recording.status === "failed"
        ? "danger"
        : recording.needsAttention
          ? "warning"
          : (BADGE_TONE[recording.status] ?? "neutral");

    return <Badge tone={tone}>{st(recording.statusLabel)}</Badge>;
  }

  switch (recording.status) {
    case "ready":
      // A recording can be playable and still have something to say — an
      // upload stored without a transcript, for instance.
      return recording.failureReason ? (
        <Badge tone="warning">{t("common:status.partial")}</Badge>
      ) : (
        <Badge tone="success">{t("common:status.ready")}</Badge>
      );
    case "recording":
      return <Badge tone="accent">{t("common:status.recording")}</Badge>;
    case "processing":
      return <Badge tone="warning">{t("common:status.processing")}</Badge>;
    case "failed":
      return <Badge tone="danger">{t("common:status.failed")}</Badge>;
    default:
      return recording.needsAttention ? (
        <Badge tone="warning">{t("common:status.waitingToBeLetIn")}</Badge>
      ) : (
        <Badge>{t("common:status.scheduled")}</Badge>
      );
  }
}

/** The sentence under the badge. Null when the status speaks for itself. */
export function statusExplanation(recording: RecordingSummary): string | null {
  if (hasStalled(recording)) {
    return i18n.t("recording:status.stalled");
  }
  // The server derives this from the provider's own status, so it is the most
  // specific thing available. Uploads have none and fall through.
  if (recording.statusMessage) return serverText(recording.statusMessage);
  if (recording.status === "processing") {
    return i18n.t("recording:status.transcribing");
  }
  return recording.failureReason ? serverText(recording.failureReason) : null;
}
